import { useCallback, useState } from "react";

import { clamp } from "lodash-es";
import { useEventListener, useLocalStorage } from "usehooks-ts";

type UseResizableWidthOptions = {
  // Viewport edge the pane is anchored to. Determines cursor math:
  // "right" → dragging left grows width; "left" → dragging right grows width.
  side: "left" | "right";
  storageKey: string;
  minWidth: number;
  maxWidth: number;
  defaultWidth?: number;
};

type UseResizableWidthResult = {
  width: number;
  isResizing: boolean;
  handleResizeStart: () => void;
};

export const useResizableWidth = ({
  side,
  storageKey,
  minWidth,
  maxWidth,
  defaultWidth,
}: UseResizableWidthOptions): UseResizableWidthResult => {
  const effectiveDefault = defaultWidth ?? (minWidth + maxWidth) / 2;
  const [width, setWidth] = useLocalStorage<number>(
    storageKey,
    effectiveDefault,
  );
  const clampedWidth = clamp(width, minWidth, maxWidth);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEventListener("mousemove", (event: MouseEvent) => {
    if (!isResizing) return;
    const raw =
      side === "right" ? window.innerWidth - event.clientX : event.clientX;
    setWidth(clamp(raw, minWidth, maxWidth));
  });

  useEventListener("mouseup", () => {
    if (!isResizing) return;
    setIsResizing(false);
  });

  return { width: clampedWidth, isResizing, handleResizeStart };
};
